import { t } from "@lingui/macro";
import type { GetGrokbotToolSetupResponse } from "@repo/common";
import {
	configureGrokbotTool,
	disconnectGrokbotTool,
	getGrokbotToolSetup,
} from "@repo/ui";
import {
	AlertCircle,
	Bot,
	CheckCircle2,
	KeyRound,
	Loader2,
	RefreshCw,
	Unlink,
} from "lucide-react";
import React from "react";
import { toast } from "sonner";
import type { Agent } from "../types";
import { CollapsibleSettingsPanel } from "./CollapsibleSettingsPanel";
import { ToolLoadingCard } from "./ToolLoadingCard";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

interface GrokbotSettingsCardProps {
	companyId: string;
	agents: Agent[];
}

function stateLabel(state: GetGrokbotToolSetupResponse["state"]): string {
	switch (state) {
		case "not_available":
			return t({ id: "grokbotNotAvailable", message: "Not available" });
		case "available_not_configured":
			return t({
				id: "grokbotNotConfigured",
				message: "Available, not configured",
			});
		case "available_configured":
			return t({ id: "grokbotConfigured", message: "Configured" });
		case "configuration_error":
			return t({
				id: "grokbotConfigurationError",
				message: "Configuration error",
			});
	}
}

export function GrokbotSettingsCard({
	companyId,
	agents,
}: GrokbotSettingsCardProps) {
	const [setup, setSetup] = React.useState<GetGrokbotToolSetupResponse>();
	const [selectedAgentIds, setSelectedAgentIds] = React.useState<Set<string>>(
		new Set(),
	);
	const [wakeUrl, setWakeUrl] = React.useState("");
	const [bearerToken, setBearerToken] = React.useState("");
	const [editingCredentials, setEditingCredentials] = React.useState(false);
	const [isLoading, setIsLoading] = React.useState(true);
	const [action, setAction] = React.useState<"save" | "disconnect" | null>(
		null,
	);
	const [showDisconnectDialog, setShowDisconnectDialog] = React.useState(false);

	const loadSetup = React.useCallback(
		async (showError = true) => {
			setIsLoading(true);
			try {
				const result = await getGrokbotToolSetup({ companyId });
				setSetup(result.data);
				setSelectedAgentIds(
					new Set(
						result.data.bindings
							.filter((binding) => binding.enabled)
							.map((binding) => binding.agentId),
					),
				);
				setEditingCredentials(!result.data.hasCredentials);
			} catch (error) {
				console.error("Failed to load Grokbot configuration", error);
				if (showError) {
					toast.error(
						t({
							id: "grokbotConfigurationLoadFailed",
							message: "Could not load the Grokbot configuration.",
						}),
					);
				}
			} finally {
				setIsLoading(false);
			}
		},
		[companyId],
	);

	React.useEffect(() => {
		void loadSetup();
	}, [loadSetup]);

	const toggleAgent = (agentId: string, enabled: boolean) => {
		setSelectedAgentIds((current) => {
			const next = new Set(current);
			if (enabled) next.add(agentId);
			else next.delete(agentId);
			return next;
		});
	};

	const handleSave = async () => {
		if (!setup) return;
		const replacingCredentials = !setup.hasCredentials || editingCredentials;
		if (replacingCredentials && (!wakeUrl.trim() || !bearerToken.trim()))
			return;
		setAction("save");
		try {
			await configureGrokbotTool({
				companyId,
				agentIds: [...selectedAgentIds],
				...(replacingCredentials
					? { wakeUrl: wakeUrl.trim(), bearerToken: bearerToken.trim() }
					: {}),
			});
			setWakeUrl("");
			setBearerToken("");
			await loadSetup(false);
			toast.success(
				t({
					id: "grokbotConfigurationSaved",
					message: "Grokbot configuration saved.",
				}),
			);
		} catch (error) {
			console.error("Failed to save Grokbot configuration", error);
			toast.error(
				t({
					id: "grokbotConfigurationSaveFailed",
					message: "Could not save the Grokbot configuration.",
				}),
			);
		} finally {
			setAction(null);
		}
	};

	const handleDisconnect = async () => {
		setAction("disconnect");
		try {
			await disconnectGrokbotTool({ companyId });
			setShowDisconnectDialog(false);
			setWakeUrl("");
			setBearerToken("");
			await loadSetup(false);
			toast.success(
				t({ id: "grokbotDisconnected", message: "Grokbot disconnected." }),
			);
		} catch (error) {
			console.error("Failed to disconnect Grokbot", error);
			toast.error(
				t({
					id: "grokbotDisconnectFailed",
					message: "Could not disconnect Grokbot.",
				}),
			);
		} finally {
			setAction(null);
		}
	};

	if (isLoading && !setup) return <ToolLoadingCard title="Grokbot" />;
	if (setup?.state === "not_available") return null;
	const showCredentials = setup
		? !setup.hasCredentials || editingCredentials
		: false;
	const saveDisabled =
		action !== null ||
		(showCredentials && (!wakeUrl.trim() || !bearerToken.trim()));

	return (
		<CollapsibleSettingsPanel
			icon={<Bot className="h-5 w-5" />}
			title="Grokbot"
			description={t({
				id: "grokbotDescription",
				message:
					"Let selected agents send complete questions to Grokbot and relay its answers during calls.",
			})}
			headerAction={
				setup ? (
					<Badge
						variant={
							setup.state === "configuration_error"
								? "destructive"
								: setup.state === "available_configured"
									? "default"
									: "secondary"
						}
					>
						{setup.state === "available_configured" && (
							<CheckCircle2 className="h-3 w-3" />
						)}
						{setup.state === "configuration_error" && (
							<AlertCircle className="h-3 w-3" />
						)}
						{stateLabel(setup.state)}
					</Badge>
				) : null
			}
		>
			<div className="space-y-5">
				{setup ? (
					<>
						{setup.state === "configuration_error" && (
							<div className="flex gap-2 rounded-md border border-destructive/30 bg-danger-tint p-3 text-sm text-danger-ink">
								<AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
								<span>
									{t({
										id: "grokbotConfigurationNeedsAttention",
										message:
											"Grokbot configuration needs attention. Replace the credentials and try again.",
									})}
								</span>
							</div>
						)}

						{setup.hasCredentials && !editingCredentials ? (
							<div className="flex flex-wrap items-center gap-2">
								<Button
									variant="outline"
									onClick={() => setEditingCredentials(true)}
									disabled={action !== null}
								>
									<KeyRound className="me-2 h-4 w-4" />
									{t({
										id: "replaceGrokbotCredentials",
										message: "Replace credentials",
									})}
								</Button>
								<Button
									variant="outline"
									onClick={() => setShowDisconnectDialog(true)}
									disabled={action !== null}
								>
									<Unlink className="me-2 h-4 w-4" />
									{t({
										id: "disconnectGrokbot",
										message: "Disconnect Grokbot",
									})}
								</Button>
							</div>
						) : (
							<form
								id="grokbot-credentials-form"
								className="space-y-4 rounded-md border-[1.5px] border-card-border shadow-card p-4"
								onSubmit={(event) => {
									event.preventDefault();
									void handleSave();
								}}
							>
								<div className="space-y-1">
									<p className="text-sm font-medium">
										{setup.hasCredentials
											? t({
													id: "replaceGrokbotCredentialsTitle",
													message: "Replace Grokbot credentials",
												})
											: t({
													id: "connectGrokbot",
													message: "Connect Grokbot",
												})}
									</p>
									<p className="text-xs text-muted-foreground">
										{t({
											id: "grokbotCredentialsPrivacy",
											message:
												"Credentials are encrypted when saved and are never displayed again.",
										})}
									</p>
								</div>
								<div className="space-y-2">
									<Label htmlFor="grokbot-wake-url">
										{t({ id: "grokbotWakeUrl", message: "Wake URL" })}
									</Label>
									<Input
										id="grokbot-wake-url"
										name="grokbot-wake-url"
										type="url"
										dir="ltr"
										autoComplete="url"
										placeholder="https://…"
										value={wakeUrl}
										onChange={(event) => setWakeUrl(event.target.value)}
										disabled={action !== null}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="grokbot-bearer-token">
										{t({ id: "grokbotBearerToken", message: "Bearer token" })}
									</Label>
									<Input
										id="grokbot-bearer-token"
										name="grokbot-bearer-token"
										type="password"
										dir="ltr"
										autoComplete="new-password"
										value={bearerToken}
										onChange={(event) => setBearerToken(event.target.value)}
										disabled={action !== null}
									/>
								</div>
								{setup.hasCredentials && (
									<Button
										type="button"
										variant="ghost"
										onClick={() => {
											setEditingCredentials(false);
											setWakeUrl("");
											setBearerToken("");
										}}
										disabled={action !== null}
									>
										{t({ id: "cancel", message: "Cancel" })}
									</Button>
								)}
							</form>
						)}

						<fieldset className="space-y-3">
							<legend className="text-sm font-medium">
								{t({
									id: "grokbotEnabledAgents",
									message: "Agents with Grokbot access",
								})}
							</legend>
							{agents.length === 0 ? (
								<p className="text-sm text-muted-foreground">
									{t({
										id: "grokbotNoAgents",
										message: "Create an agent before enabling Grokbot access.",
									})}
								</p>
							) : (
								<div className="divide-y rounded-md border">
									{agents.map((agent) => (
										<div key={agent.id} className="flex items-center gap-3 p-3">
											<Checkbox
												id={`grokbot-agent-${agent.id}`}
												checked={selectedAgentIds.has(agent.id)}
												onCheckedChange={(checked) =>
													toggleAgent(agent.id, checked === true)
												}
												disabled={action !== null}
											/>
											<Label htmlFor={`grokbot-agent-${agent.id}`}>
												{agent.name}
											</Label>
										</div>
									))}
								</div>
							)}
						</fieldset>

						<div className="flex flex-wrap gap-2">
							<Button
								type={showCredentials ? "submit" : "button"}
								form={showCredentials ? "grokbot-credentials-form" : undefined}
								onClick={showCredentials ? undefined : () => void handleSave()}
								disabled={saveDisabled}
							>
								{action === "save" && (
									<Loader2 className="me-2 h-4 w-4 animate-spin" />
								)}
								{t({
									id: "saveGrokbotConfiguration",
									message: "Save Grokbot configuration",
								})}
							</Button>
							<Button
								variant="ghost"
								onClick={() => void loadSetup()}
								disabled={isLoading || action !== null}
							>
								<RefreshCw
									className={`me-2 h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
								/>
								{t({ id: "refresh", message: "Refresh" })}
							</Button>
						</div>
					</>
				) : (
					<div className="space-y-3">
						<p role="alert" className="text-sm text-destructive">
							{t({
								id: "settings.toolLoadFailed",
								message: "Could not load tool settings. Please retry.",
							})}
						</p>
						<Button variant="outline" onClick={() => void loadSetup()}>
							<RefreshCw className="me-2 h-4 w-4" />
							{t({ id: "retry", message: "Retry" })}
						</Button>
					</div>
				)}
			</div>

			<AlertDialog
				open={showDisconnectDialog}
				onOpenChange={setShowDisconnectDialog}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t({
								id: "disconnectGrokbotTitle",
								message: "Disconnect Grokbot?",
							})}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t({
								id: "disconnectGrokbotDescription",
								message:
									"This removes the stored credentials and disables Grokbot for all agents.",
							})}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={action === "disconnect"}>
							{t({ id: "cancel", message: "Cancel" })}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={(event) => {
								event.preventDefault();
								void handleDisconnect();
							}}
							disabled={action === "disconnect"}
						>
							{action === "disconnect" && (
								<Loader2 className="me-2 h-4 w-4 animate-spin" />
							)}
							{t({
								id: "disconnectGrokbot",
								message: "Disconnect Grokbot",
							})}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</CollapsibleSettingsPanel>
	);
}
